import type { ReactNode } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import type { Combatant, EncounterState } from "@lancer-vtt/domain";
import type { LoadedOverlay } from "@lancer-vtt/rules";
import type { UseEncounter } from "../useEncounter";
import { setTrackCommand } from "../tracks";
import type { ActionDraft } from "../combatInspector";
import { TurnControls } from "./TurnControls";
import { UnitSheet, UnitSheetStatuses, type UnitSheetIdentity } from "./UnitSheet";
import { SkirmishChooser } from "./SkirmishChooser";
import { PreparedHoldPanel } from "./inspectorPanels";
import { useUnitActionModel } from "./useUnitActionModel";
import { UnitSheetVitals } from "./unitVitals";
import { descriptiveStatCells, StatStrip } from "./unitStats";
import { UnitLoadout } from "./UnitLoadout";
import { pilotLoadout } from "../unitLoadout";
// Type-only — see {@link MechInspectorBody}'s identical import.
import type { PreselectBonus } from "../../../routes/home/DiceApp";

/**
 * The **pilot (on-foot)** sheet, routed from {@link InspectorBody} when `combatant.kind === "pilot"`.
 * A pilot lands on the board via Dismount/Eject or a direct GM drop. It fills the {@link UnitSheet}
 * shell with what a pilot's frozen base carries: HP and burn, the descriptive stats, the statuses,
 * and the loadout ({@link UnitLoadout}: weapons, armor and gear, {@link pilotLoadout}).
 * No heat, structure, stress, repair or overshield: a pilot on foot has none of those tracks
 * (`spawnPilot`).
 *
 * **Actions are not here, and the sheet has no action rows at all** (developer ruling, 2026-09-25).
 * The token command menu (`TokenCommandMenu.tsx`) is the pilot's one action surface, as it is for
 * mechs and NPCs: the sheet's QUICK / FULL / REACTION grid and the choosers it armed were retired in
 * `pilot-command-menu` Phase 3. The prepared hold renders read-only (badge and trigger): the menu's
 * root carries its Release and Drop. One chooser stays: the Overwatch take
 * (`CombatApp`'s `startOverwatchTake`) and the GM advisor arm a Skirmish with a fixed target, which
 * the room books as `fight` for a pilot, and neither comes from the menu.
 */
export function PilotInspectorBody({
  combatant: c,
  identity,
  state,
  actorId,
  encounter,
  aim,
  setAim,
  fireAt,
  onDeclineOverwatch,
  canControl,
  gm,
  gearIds,
  error,
  compendium,
  overlay,
  loadingOverlay,
  onOpenDice,
}: {
  combatant: Combatant;
  /** The sheet header's identity, derived once by {@link InspectorBody}. */
  identity: UnitSheetIdentity;
  state: EncounterState;
  actorId: string;
  encounter: UseEncounter;
  aim: ActionDraft | null;
  setAim: (a: ActionDraft | null) => void;
  /** Fire the armed action at a target id — the host's `fireAt`, for the fixed-target {@link SkirmishChooser}'s FIRE button. */
  fireAt: (targetId: string) => void;
  /** The Overwatch take's explicit decline (the {@link SkirmishChooser}'s ×): the only local gesture that answers the checkpoint on the server. */
  onDeclineOverwatch?: () => void;
  canControl: boolean;
  /** The GM strip (`UnitGmStrip`), built by {@link InspectorBody} for the GM's sheet only. */
  gm?: ReactNode;
  /** The roster pilot's `gear` ids, for the loadout's ARMOR and GEAR rows (see {@link pilotLoadout}). */
  gearIds: readonly string[];
  error: string | null;
  compendium: Compendium;
  /** The merged action overlay a granted row's `resolvable` is derived against. */
  overlay: LoadedOverlay;
  /** True until {@link overlay}'s sidecar fetch has settled — see {@link CompendiumState.loadingOverlay}. */
  loadingOverlay: boolean;
  /**
   * Open COMBAT.app's own DICE.app window pre-filled — see {@link MechInspectorBody}'s identical
   * prop for the required-nullable rationale. On foot the ONE rollable cell is GRIT, which is also
   * the number that genuinely governs an unmounted character's rolls (core book: "Unmounted
   * characters make all attacks and saves by adding GRIT to the roll instead of using their
   * triggers", `decisions/0163`'s kept carve-out). There is no HASE row here to roll — this body's
   * strip comment above says why.
   */
  onOpenDice: ((preselect: PreselectBonus) => void) | null;
}) {
  const setTrack = (kind: "hp" | "heat", value: number) => encounter.dispatch(setTrackCommand({ actorId, combatantId: c.id, kind, value }));

  // `grit: true`: an on-foot pilot's `base.attackBonus` IS grit (`ComputedPilotStats.attackBonus`),
  // and on foot GRIT is the number that governs their rolls.
  //
  // **No HASE row here, unlike the mech body.** `spawnPilot` and `spawnDismountedPilot` write GRIT
  // into `hull`/`agility`/`systems` (GRIT stands in for the Search/Grapple contest axes on foot) and
  // freeze no `engineering`, so a HASE row would print the same grit three times under three labels.
  const stats = descriptiveStatCells(c.base, { grit: true });

  // The shared action model, read here only for the turn controls, the hold and the Overwatch take.
  const { isActiveActor, endTurn, isArmed, endAttack, held, releaseArmed, armRelease, dropHeld } = useUnitActionModel({
    combatant: c,
    state,
    actorId,
    isGm: encounter.isGm,
    canControl,
    compendium,
    overlay,
    loadingOverlay,
    encounter,
    aim,
    setAim,
  });

  return (
    <UnitSheet
      identity={identity}
      gm={gm}
      className="unit-sheet-control"
      stateLine={
        // The state line, END TURN (a pass on a READY unit), and the GM's START TURN on a player's unit
        // (`TurnControls`). There is no ACTIVATE TURN: any other action on a ready unit starts its
        // turn through the pre-activation fold (`token-command-menu` 2c).
        <TurnControls combatantId={c.id} state={state} actorId={actorId} encounter={encounter} canControl={canControl} isActiveActor={isActiveActor} onEndTurn={endTurn} noun="pilot" />
      }
      error={error}
      hold={
        held && (
          <PreparedHoldPanel
            held={held}
            weapons={c.base.weapons ?? []}
            releaseArmed={releaseArmed}
            // Read-only: the command menu's root carries a pilot's Release and Drop.
            canControl={false}
            onArmRelease={armRelease}
            onDrop={dropHeld}
          />
        )
      }
    >
      {/* HP plus the marked-burn chip — no heat/structure/stress/repair-cap/overshield: a pilot on
          foot carries none of those tracks (`spawnPilot`: Biological ⇒ no `maxHeat`,
          `destructionOutcome: "downAndOut"` ⇒ no structure/stress, no Repair Cap resource, no
          OVERSHIELD resource). Burn is the exception: a pilot has no heat track to confuse it with,
          and burn-typed damage marks them like anything else. */}
      <UnitSheetVitals
        hp={c.current.hp}
        maxHp={c.base.maxHp}
        exposed={false}
        shield={0}
        burn={c.current.burn ?? 0}
        onSetTrack={canControl ? setTrack : null}
        disabled={!actorId}
      />

      <StatStrip cells={stats} onOpenDice={onOpenDice} />

      <UnitSheetStatuses combatant={c} edit={encounter.isGm ? { state, actorId, dispatch: encounter.dispatch } : null} />

      {/* The Overwatch take and the GM advisor arm a Skirmish with a fixed target; neither comes
          from the command menu, so their chooser (weapon pick + FIRE at the pinned target) stays. */}
      {canControl && isArmed && aim !== null && aim.mode === "skirmish" && (aim.reaction === true || aim.fixedTargetId !== undefined) && (
        <div className="unit-sheet-armed-wrap">
          <SkirmishChooser combatant={c} state={state} aim={aim} setAim={setAim} onEndAttack={endAttack} onFire={fireAt} onDeclineOverwatch={onDeclineOverwatch} />
        </div>
      )}
      {/* The loadout (7.1), read-only: weapons, armor and gear with each row's rule text a click
          away. The command menu's Gear group is where gear is used. */}
      <UnitLoadout groups={pilotLoadout({ combatant: c, state, compendium, gearIds })} />
    </UnitSheet>
  );
}
